import { useMemo } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type {
  LoadBoolean,
  LoadJSON,
  LoadNumber,
  LoadString,
  RemoveItem,
  SaveBoolean,
  SaveJSON,
  SaveNumber,
  SaveString,
  UseStorageProps,
  UseStorageResponse,
} from './types.ts';

// eslint-disable-next-line no-console
const processError = (error: unknown) => console.error(error);

const generateKey = <TKey extends string, TScope extends string>(scope: TScope | undefined, key: TKey) => (scope ? `${scope}_${key}` : key);

const getItem = <TKey extends string, TScope extends string>(storage: Storage, key: TKey, scope: TScope | undefined): string | null => {
  const scopedKey = generateKey(scope, key);
  return storage.getItem(scopedKey);
};

const getItemSafe = <TKey extends string, TScope extends string>(storage: Storage, key: TKey, scope: TScope | undefined): string | null => {
  try {
    return getItem(storage, key, scope);
  } catch (e) {
    processError(e);
    return null;
  }
};

const removeItemSafe = <TKey extends string, TScope extends string>(storage: Storage, key: TKey, scope: TScope | undefined) => {
  try {
    const scopedKey = generateKey(scope, key);
    storage.removeItem(scopedKey);
  } catch (e) {
    processError(e);
  }
};

const setItemSafe = <TKey extends string, TScope extends string>(storage: Storage, key: TKey, scope: TScope | undefined, value: string) => {
  try {
    const scopedKey = generateKey(scope, key);
    storage.setItem(scopedKey, value);
  } catch (e) {
    processError(e);
  }
};

const useStorage = <TKey extends string, TScope extends string = string>({
  scope,
  storage,
}: UseStorageProps<TScope>): UseStorageResponse<TKey> => {
  const removeItem = useCallbackRef<RemoveItem<TKey>>((key, customScope) => removeItemSafe(storage, key, customScope ?? scope));

  const loadString = useCallbackRef<LoadString<TKey>>((key) => getItemSafe(storage, key, scope));

  const loadNumber = useCallbackRef<LoadNumber<TKey>>((key) => {
    const value = getItemSafe(storage, key, scope);
    return value !== null ? +value : null;
  });

  const loadBoolean = useCallbackRef<LoadBoolean<TKey>>((key) => !!getItemSafe(storage, key, scope));

  const loadJSON = useCallbackRef<LoadJSON<TKey>>((key, onError, customScope) => {
    try {
      const value = getItem(storage, key, customScope ?? scope);
      return value && JSON.parse(value);
    } catch (error) {
      onError?.(error);
      return null;
    }
  });

  const saveString = useCallbackRef<SaveString<TKey>>((key, data) => setItemSafe(storage, key, scope, data));

  const saveNumber = useCallbackRef<SaveNumber<TKey>>((key, data) => setItemSafe(storage, key, scope, data.toString()));

  const saveBoolean = useCallbackRef<SaveBoolean<TKey>>((key, data) => {
    if (loadBoolean(key)) {
      removeItem(key);
      return;
    }

    setItemSafe(storage, key, scope, data.toString());
  });

  const saveJSON = useCallbackRef<SaveJSON<TKey>>((key, data, customScope) =>
    storage.setItem(generateKey(customScope ?? scope, key), JSON.stringify(data)),
  );

  return useMemo<UseStorageResponse<TKey>>(
    () => ({
      removeItem,
      loadString,
      loadNumber,
      loadBoolean,
      loadJSON,
      saveString,
      saveNumber,
      saveBoolean,
      saveJSON,
    }),
    [removeItem, loadString, loadNumber, loadBoolean, loadJSON, saveString, saveNumber, saveBoolean, saveJSON],
  );
};

export default useStorage;
